import BudgetApprovalReview from "./BudgetApprovalReview";
import { budgetAlert, BudgetFeedback } from "./BudgetAlert";
import BudgetFileUpload from "./BudgetFileUpload";
import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "react-modal";

// Components จาก Template ของโปรเจกต์
import Grid from "components/Grid/GridContainer.js";
import Col from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import Button from "components/CustomButtons/Button.js";

import RequestItemForm from "./RequestItemForm";
import { BudgetService } from "_services/budget.service";
import { useBudgetCategories, PERSONNEL_CATEGORY, getItemCategoryKey } from "./budgetCategories";

const styles = {
    cardTitleWhite: {
        color: "#FFFFFF",
        marginTop: "0px",
        minHeight: "auto",
        fontWeight: "300",
        fontFamily: '"Sarabun", sans-serif',
        marginBottom: "3px"
    },
    sectionTitle: {
        borderBottom: "2px solid #00acc1",
        paddingBottom: "8px",
        marginBottom: "15px",
        fontWeight: "bold",
        color: "#00acc1",
        marginTop: "20px"
    },
    modalOverlay: {
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 1050
    },
    modalContent: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        width: "calc(100% - 32px)",
        maxWidth: "960px",
        maxHeight: "90vh",
        overflowY: "auto",
        padding: "25px",
        borderRadius: "12px",
        boxSizing: "border-box",
        fontFamily: '\"Sarabun\", sans-serif',
        fontSize: "15px",
        lineHeight: 1.6
    },
    detailSection: {
        padding: "18px",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        marginTop: "18px",
        backgroundColor: "#fff",
        "& h5": { fontSize: "16px", fontWeight: 600, margin: "0 0 14px", color: "#334155" },
        "& label": { fontSize: "14px", fontWeight: 500, lineHeight: 1.6, marginBottom: "8px" },
        "& .form-control": { fontSize: "15px", minHeight: "42px" },
        "& [class*=col-]": { marginBottom: "12px" }
    },
    quantityGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(var(--quantity-columns), minmax(0, 1fr))",
        gap: "16px",
        "& label": { display: "block", whiteSpace: "nowrap" },
        "@media (max-width: 700px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
        "@media (max-width: 420px)": { gridTemplateColumns: "minmax(0, 1fr)" }
    },
    checklistGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: "12px",
        "@media (max-width: 640px)": { gridTemplateColumns: "minmax(0, 1fr)" }
    },
    checklistOption: {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "14px 16px",
        minHeight: "54px",
        margin: "0 !important",
        cursor: "pointer",
        border: "1px solid #dbe3eb",
        borderRadius: "8px",
        backgroundColor: "#f8fafc",
        "&:hover": { borderColor: "#00acc1", backgroundColor: "#effbfc" },
        "&:focus-within": { outline: "2px solid #00acc1", outlineOffset: "2px" },
        "& input[type=checkbox]": {
            width: "22px", minWidth: "22px", height: "22px", flexShrink: 0,
            margin: "1px 0 0", cursor: "pointer", accentColor: "#00acc1"
        },
        "& span": { fontSize: "15px", lineHeight: 1.6, color: "#334155", wordBreak: "normal" }
    },
    modalActions: {
        position: "sticky", bottom: "-25px", backgroundColor: "#fff", zIndex: 2,
        display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: "8px",
        borderTop: "1px solid #e2e8f0", padding: "16px 0 0", marginTop: "24px"
    }
};

const useStyles = makeStyles(styles);

// Mockup ข้อมูลหน่วยงาน
const mockMissions = [
    { key: 1, name: "ภารกิจด้านวิชาการและการแพทย์" },
    { key: 2, name: "ภารกิจด้านอำนวยการ" },
    { key: 3, name: "ภารกิจด้านการพยาบาล" },
    { key: 5, name: "ภารกิจด้านการพัฒนาระบบสุขภาพ" }
];
const mockGroupWorks = [
    { key: 23, missionKey: 5, name: "กลุ่มงานดิจิทัลการแพทย์" },
    { key: 1, missionKey: 5, name: "กลุ่มงานวิจัย ถ่ายทอด และสนับสนุนวิชาการ" },
    { key: 2, missionKey: 2, name: "กลุ่มงานบริหารทั่วไป" },
    { key: 7, missionKey: 2, name: "กลุ่มงานการเงิน บัญชี และพัสดุ" }
];
const mockDepartments = [
    { key: 108, groupWorkKey: 23, name: "งานคอมพิวเตอร์" },
    { key: 118, groupWorkKey: 23, name: "งานเวชระเบียน" },
    { key: 105, groupWorkKey: 2, name: "งานทรัพยากรบุคคล" },
    { key: 127, groupWorkKey: 7, name: "งานพัสดุและบำรุงรักษา" }
];

const BudgetForm = () => {
    const { subcategoryDetails, CAPITAL_CATEGORIES, OPERATING_CATEGORIES, isCapitalCategory, getCategoryLabel, getOperatingSubcategories, loading: categoriesLoading, error: categoriesError } = useBudgetCategories();
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();

    // รับ roundKey และ requestKey จาก Route State
    const roundKey = location.state?.roundKey || null;
    const existingRequestKey = location.state?.requestKey || null;

    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);
    const [canEdit, setCanEdit] = useState(true);
    const [approvalLevel, setApprovalLevel] = useState(0);
    const [requestStatus, setRequestStatus] = useState("Draft");
    const [isSaving, setIsSaving] = useState(false);
    const [pendingSubcategoryFiles, setPendingSubcategoryFiles] = useState({});
    const [filesRefreshKey, setFilesRefreshKey] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [requestKey, setRequestKey] = useState(existingRequestKey);

    // 1. Header หน่วยงาน
    const [headerData, setHeaderData] = useState({
        MissionKey: 5,
        GroupWorkKey: 23,
        DepartmentKey: 108
    });
    const [filteredGroups, setFilteredGroups] = useState(
        mockGroupWorks.filter((g) => g.missionKey === 5)
    );
    const [filteredDepts, setFilteredDepts] = useState(
        mockDepartments.filter((d) => d.groupWorkKey === 23)
    );

    // ItemType identifies the leaf category; all categories share one request.
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [budgetCategory, setBudgetCategory] = useState(1);
    useEffect(() => {
        if (!categoriesLoading && budgetCategory !== 7 &&
            ![...CAPITAL_CATEGORIES, ...OPERATING_CATEGORIES].some(c => c.key === budgetCategory)) {
            setBudgetCategory(CAPITAL_CATEGORIES[0]?.key || OPERATING_CATEGORIES[0]?.key || 7);
        }
    }, [categoriesLoading, CAPITAL_CATEGORIES, OPERATING_CATEGORIES, budgetCategory]);

    // 3. โครงการงบลงทุน (ครุภัณฑ์ / สิ่งก่อสร้าง / โครงการ)
    const [projectData, setProjectData] = useState({
        ProjectKey: null,
        ProjectName: "",
        Objective: "",
        StrategicAlignment: ""
    });
    const [additionalProjects, setAdditionalProjects] = useState([]);
    const [requestItems, setRequestItems] = useState([]);
    const projectItems = requestItems.filter(i => isCapitalCategory(getItemCategoryKey(i)));

    // 5. Modal สำหรับแก้ไขรายละเอียดเชิงลึก
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState({ category: 1, index: null });
    const [editFormData, setEditFormData] = useState({});

    useEffect(() => {
        Modal.setAppElement("body");
        if (existingRequestKey && !categoriesLoading && !categoriesError) {
            loadExistingRequest(existingRequestKey);
        }
    }, [existingRequestKey, categoriesLoading, categoriesError]);

    // โหลดข้อมูลเก่าด้วย requestKey โดยตรง
    const loadExistingRequest = async (key) => {
        setIsLoading(true);
        try {
            const res = await BudgetService.getRequestById(key);
            if (res.data.success && res.data.data) {
                const req = res.data.data;
                setCanEdit(res.data.canEdit !== false);
                setApprovalLevel(res.data.approvalLevel || 0);
                setRequestStatus(req.status ?? req.Status ?? "Draft");
                const reqId = req.requestKey ?? req.RequestKey;
                const missionId = req.missionKey ?? req.MissionKey;
                const groupId = req.groupWorkKey ?? req.GroupWorkKey;
                const deptId = req.departmentKey ?? req.DepartmentKey;

                setRequestKey(reqId);

                // เซต Header สังกัด
                setHeaderData({
                    MissionKey: missionId,
                    GroupWorkKey: groupId,
                    DepartmentKey: deptId
                });
                setFilteredGroups(mockGroupWorks.filter((g) => g.missionKey === missionId));
                setFilteredDepts(mockDepartments.filter((d) => d.groupWorkKey === groupId));

                // ฟังก์ชันแปลง Object Item ให้ปลอดภัยต่อ Case Sensitivity
                const mapItemToState = (i) => ({
                    ItemKey: i.itemKey ?? i.ItemKey,
                    ClientKey: `item-${i.itemKey ?? i.ItemKey}`,
                    ProjectKey: i.projectKey ?? i.ProjectKey ?? null,
                    ItemName: i.itemName ?? i.ItemName ?? "",
                    ItemType: Number(i.itemType ?? i.ItemType ?? 1),
                    ItemCategory: i.itemCategory ?? i.ItemCategory ?? getCategoryLabel(i.itemType ?? i.ItemType ?? 1),
                    DepartmentReviews: i.departmentReviews ?? i.DepartmentReviews ?? [],
                    FileName: i.fileName ?? i.FileName ?? "",
                    TargetMonth: i.targetMonth ?? i.TargetMonth ?? "",
                    ExistingQty: i.existingQty ?? i.ExistingQty ?? 0,
                    TotalRequiredQty: i.totalRequiredQty ?? i.TotalRequiredQty ?? 0,
                    ReplacementQty: i.replacementQty ?? i.ReplacementQty ?? 0,
                    NewPurchaseQty: i.newPurchaseQty ?? i.NewPurchaseQty ?? 0,
                    RequestQty: i.requestQty ?? i.RequestQty ?? 0,
                    UnitPrice: parseFloat(i.unitPrice ?? i.UnitPrice ?? 0),
                    TotalAmount: parseFloat(i.totalAmount ?? i.TotalAmount ?? 0),
                    OldItemAge: i.oldItemAge ?? i.OldItemAge ?? "",
                    Reason: i.reason ?? i.Reason ?? "",
                    IsStandardPrice: i.isStandardPrice ?? i.IsStandardPrice ?? false,
                    HasSpec: i.hasSpec ?? i.HasSpec ?? false,
                    HasQuotation: i.hasQuotation ?? i.HasQuotation ?? false,
                    HasTechAssessment: i.hasTechAssessment ?? i.HasTechAssessment ?? false,
                    HasBlueprint: i.hasBlueprint ?? i.HasBlueprint ?? false,
                    HasBOQ: i.hasBOQ ?? i.HasBOQ ?? false
                });

                const projects = req.projects ?? req.Projects ?? [];
                const mapProject = p => ({
                    ProjectKey: p.projectKey ?? p.ProjectKey,
                    ProjectName: p.projectName ?? p.ProjectName ?? "",
                    Objective: p.objective ?? p.Objective ?? "",
                    StrategicAlignment: p.strategicAlignment ?? p.StrategicAlignment ?? ""
                });
                if (projects.length > 0) {
                    setProjectData(mapProject(projects[0]));
                }
                setAdditionalProjects(projects.slice(1).map(mapProject));

                // Deduplicate the same item returned under Request.Items and Project.Items.
                const allItems = req.items ?? req.Items ?? [];
                const projectListItems = projects.flatMap(p => (p.items ?? p.Items ?? []).map(i => ({
                    ...i, ProjectKey: i.projectKey ?? i.ProjectKey ?? p.projectKey ?? p.ProjectKey
                })));
                const uniqueItems = new Map([...allItems, ...projectListItems].map(i => {
                    const item = mapItemToState(i);
                    return [item.ItemKey, item];
                }));
                setRequestItems(Array.from(uniqueItems.values()));
            }
        } catch (error) {
            console.error("Load request error:", error);
            budgetAlert.error("ไม่สามารถดึงข้อมูลเดิมได้: " + (error.response?.data?.message || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    // ฟังก์ชันช่วยดึงและอัปเดต List ตาม Category ปัจจุบัน
    const getActiveList = (cat = budgetCategory) => requestItems.filter(i => getItemCategoryKey(i) === cat);
    const getSubcategoryCounts = cat => getActiveList(cat).reduce((counts, item) => {
        counts[item.ItemCategory] = (counts[item.ItemCategory] || 0) + 1;
        return counts;
    }, Object.create(null));

    const setActiveList = (cat, updater) => {
        setRequestItems(prev => [
            ...prev.filter(i => getItemCategoryKey(i) !== cat),
            ...updater(prev.filter(i => getItemCategoryKey(i) === cat))
        ]);
    };

    // Dropdown Handlers
    const handleMissionChange = (e) => {
        const val = parseInt(e.target.value);
        setHeaderData({
            ...headerData,
            MissionKey: val,
            GroupWorkKey: "",
            DepartmentKey: ""
        });
        setFilteredGroups(mockGroupWorks.filter((g) => g.missionKey === val));
        setFilteredDepts([]);
    };

    const handleGroupWorkChange = (e) => {
        const val = parseInt(e.target.value);
        setHeaderData({ ...headerData, GroupWorkKey: val, DepartmentKey: "" });
        setFilteredDepts(mockDepartments.filter((d) => d.groupWorkKey === val));
    };

    const handleDepartmentChange = (e) =>
        setHeaderData({ ...headerData, DepartmentKey: parseInt(e.target.value) });

    // Table & Modal Handlers
    const handleAddItem = (newItem) => {
        setActiveList(budgetCategory, (prev) => [...prev, { ...newItem, ClientKey: `new-${Date.now()}-${Math.random().toString(36).slice(2)}` }]);
    };

    const handleRemoveItem = (indexToRemove) => {
        setActiveList(budgetCategory, (prev) => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleOpenEdit = (index) => {
        const currentList = getActiveList(budgetCategory);
        setEditTarget({ category: budgetCategory, index });
        setEditFormData({ ...currentList[index] });
        setIsEditModalOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData({
            ...editFormData,
            [name]: type === "checkbox" ? checked : value
        });
    };

    const handleSaveEdit = () => {
        if (getOperatingSubcategories(editTarget.category).length && !editFormData.ItemCategory) {
            return budgetAlert.warning("กรุณาเลือกหมวดย่อยของรายการ");
        }
        const isDurable = isCapitalCategory(editTarget.category);
        const repQty = isDurable ? parseInt(editFormData.ReplacementQty || 0) : 0;
        const newQty = parseInt(editFormData.NewPurchaseQty || 0);
        const price = parseFloat(editFormData.UnitPrice || 0);
        const reqQty = repQty + newQty;

        const updatedData = {
            ...editFormData,
            ReplacementQty: repQty,
            RequestQty: reqQty,
            TotalAmount: reqQty * price
        };

        setActiveList(editTarget.category, (prev) => {
            const updated = [...prev];
            updated[editTarget.index] = updatedData;
            return updated;
        });

        setIsEditModalOpen(false);
    };

    // บันทึกส่ง Backend รวมทุกหมวด
    const handleSave = async (status, stay = false) => {
        if (isSaving || !canEdit) return;
        const totalItemsCount = requestItems.length;

        if (totalItemsCount === 0) {
            return budgetAlert.warning("กรุณาเพิ่มรายการคำขออย่างน้อย 1 รายการก่อนบันทึก");
        }

        if (projectItems.length > 0 && !projectData.ProjectName.trim()) {
            return budgetAlert.warning("กรุณาระบุชื่อโครงการ");
        }

        const itemsToSave = requestItems.map(i => ({ ...i, ClientKey: i.ClientKey || `item-${i.ItemKey}` }));
        const allDirectItems = itemsToSave.filter(i => !isCapitalCategory(getItemCategoryKey(i)));

        // Validate ความจำเป็นก่อน Submit
        if (status === "Submitted") {
            const allItems = [...projectItems, ...allDirectItems];
            const incomplete = allItems.find((i) => !i.Reason || i.Reason.trim() === "");
            if (incomplete) {
                return budgetAlert.warning(
                    `กรุณาระบุ "เหตุผลความจำเป็น" ของรายการ '${incomplete.ItemName}' ให้ครบถ้วนก่อนส่งคำขอ (กดปุ่มแก้ไขที่ตาราง)`
                );
            }
        }

        const hasPendingFiles = requestItems.some(i => i.PendingFile) || Object.values(pendingSubcategoryFiles).some(files => files.length);
        const itemPayload = ({ PendingFile, ...item }) => item;
        const payload = {
            RequestKey: requestKey, // ส่ง RequestKey เพื่อให้อัปเดตทับ Record เดิม
            RoundKey: roundKey,
            MissionKey: headerData.MissionKey,
            GroupWorkKey: headerData.GroupWorkKey,
            DepartmentKey: headerData.DepartmentKey,
            Status: hasPendingFiles ? "Draft" : status,
            Projects: [projectData, ...additionalProjects].map(p => ({
                ...p,
                Items: projectItems.filter(i => i.ProjectKey ? i.ProjectKey === p.ProjectKey : p === projectData).map(itemPayload)
            })).filter(p => p.Items.length > 0 || p.ProjectName.trim()),
            DirectItems: allDirectItems.map(itemPayload)
        };

        setIsSaving(true);
        try {
            const res = await BudgetService.saveBudgetRequest(payload);
            if (!res.data.success) throw new Error(res.data.message || "บันทึกคำขอไม่สำเร็จ");
            if (res.data.success) {
                if (res.data.warning) await budgetAlert.warning(res.data.warning);
                const currentReqKey = res.data.requestKey || requestKey;
                setRequestKey(currentReqKey);
                if (hasPendingFiles) {
                    const savedItems = res.data.items;
                    if (!Array.isArray(savedItems)) throw new Error("บันทึกรายการแล้ว แต่ API ไม่ส่ง ItemKey กลับมา กรุณาโหลด API รุ่นล่าสุดก่อนลองอัปโหลดอีกครั้ง");
                    const updatedItems = requestItems.map(item => {
                        const saved = savedItems.find(i => i.clientKey === item.ClientKey || (item.ItemKey && i.itemKey === item.ItemKey));
                        return saved ? { ...item, ItemKey: saved.itemKey, ProjectKey: saved.projectKey } : item;
                    });
                    setRequestItems(updatedItems);
                    const firstProject = updatedItems.find(i => isCapitalCategory(getItemCategoryKey(i)) && i.ProjectKey);
                    const savedProject = res.data.projects?.find(p => p.projectName === projectData.ProjectName);
                    const mainProjectKey = projectData.ProjectKey || savedProject?.projectKey || firstProject?.ProjectKey;
                    if (mainProjectKey) setProjectData(p => ({ ...p, ProjectKey: mainProjectKey }));
                    for (const item of updatedItems.filter(i => i.PendingFile)) {
                        if (!item.ItemKey) throw new Error(`ไม่พบ ItemKey ของ ${item.ItemName}`);
                        const upload = await BudgetService.uploadItemPdf(item.ItemKey, item.PendingFile);
                        if (!upload.data.success) throw new Error(upload.data.message || "อัปโหลดไฟล์รายการไม่สำเร็จ");
                        setRequestItems(prev => prev.map(i => i.ClientKey === item.ClientKey ? { ...i, PendingFile: null, FileName: upload.data.fileName } : i));
                    }
                    for (const [key, files] of Object.entries(pendingSubcategoryFiles)) {
                        for (const file of files) {
                            const upload = await BudgetService.uploadSubcategoryPdfs(Number(key), [file]);
                            if (!upload.data.success) throw new Error(upload.data.message || "อัปโหลดไฟล์หมวดย่อยไม่สำเร็จ");
                            setPendingSubcategoryFiles(prev => ({ ...prev, [key]: prev[key].filter(f => f !== file) }));
                            setFilesRefreshKey(prev => prev + 1);
                        }
                    }
                }
                if (status === "Submitted") {
                    await BudgetService.submitBudgetPlan(currentReqKey);
                    await budgetAlert.success("ส่งคำขอเข้าสู่กระบวนการอนุมัติเรียบร้อยแล้ว");
                } else {
                    await budgetAlert.success("บันทึกร่างคำขอเรียบร้อยแล้ว");
                }
                if (stay) await loadExistingRequest(currentReqKey);
                else history.push("/budget/index");
            }
        } catch (error) {
            budgetAlert.error("เกิดข้อผิดพลาดในการบันทึกหรืออัปโหลด (ไฟล์ที่ยังไม่สำเร็จจะรอให้กดบันทึกอีกครั้ง): " + (error.response?.data?.message || error.message));
        } finally { if (mounted.current) setIsSaving(false); }
    };

    const handleReviewSubmit = async decisions => {
        if (!approvalLevel || isSaving) return;
        setIsSaving(true);
        try {
            const res = await BudgetService.actionDepartmentApproval({ RequestKey: requestKey, CurrentLevel: approvalLevel, Items: decisions });
            if (!res.data.success) throw new Error(res.data.message || "บันทึกผลพิจารณาไม่สำเร็จ");
            await loadExistingRequest(requestKey);
            await budgetAlert.success(res.data.status === "Need_Revision" ? "ส่งกลับให้เจ้าของคำขอแก้ไขแล้ว" : "บันทึกผลการพิจารณาเรียบร้อยแล้ว");
        } catch (error) { budgetAlert.error(error.response?.data?.message || error.message); }
        finally { if (mounted.current) setIsSaving(false); }
    };

    const isDurable = isCapitalCategory(budgetCategory);
    const showItemCategory = isDurable || getOperatingSubcategories(budgetCategory).length > 0;
    const editSubcategories = getOperatingSubcategories(editTarget.category);
    const editSubcategoryCounts = getSubcategoryCounts(editTarget.category);
    const activeItems = getActiveList(budgetCategory);
    const subcategoryFilter = selectedSubcategory?.categoryKey === budgetCategory ? selectedSubcategory.label : "";
    const currentList = subcategoryFilter && getOperatingSubcategories(budgetCategory).length > 0
        ? activeItems.filter(item => item.ItemCategory === subcategoryFilter)
        : activeItems;

    if (categoriesError) return <BudgetFeedback message={categoriesError} />;
    if (isLoading || categoriesLoading) {
        return (
            <div className="text-center p-5">
                <i className="fas fa-spinner fa-spin fa-2x text-info"></i>
                <p className="mt-2 text-muted">กำลังโหลดข้อมูลคำของบประมาณเดิม...</p>
            </div>
        );
    }

    return (
        <div>
            <Grid>
                <Col xs={12}>
                    <Card>
                        <CardHeader color="primary">
                            <h4 className={classes.cardTitleWhite}>
                                {requestKey
                                    ? `${canEdit ? "แก้ไข" : "รายละเอียด"}คำของบประมาณรายจ่ายเงินบำรุง (รหัส #${requestKey})`
                                    : "บันทึกคำของบประมาณรายจ่ายเงินบำรุง (รายการใหม่)"}
                            </h4>
                        </CardHeader>
                        <CardBody>
                            <fieldset disabled={isSaving} style={{ minWidth: 0 }}>
                            {!canEdit && <p className="text-info">ดูรายละเอียดคำขอ — สถานะ: {requestStatus}</p>}
                            <fieldset disabled={!canEdit}>
                            {/* 1. Header สังกัดหน่วยงาน */}
                            <div className="p-3 mb-3 bg-light border rounded">
                                <h5 className="font-weight-bold text-info" style={{ marginTop: 0 }}>
                                    <i className="fas fa-building"></i> 1. สังกัดหน่วยงานที่ขอ
                                </h5>
                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="font-weight-bold">ภารกิจ</label>
                                        <select
                                            className="form-control"
                                            value={headerData.MissionKey}
                                            onChange={handleMissionChange}
                                        >
                                            <option value="">-- เลือกภารกิจ --</option>
                                            {mockMissions.map((m) => (
                                                <option key={m.key} value={m.key}>
                                                    {m.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="font-weight-bold">กลุ่มงาน</label>
                                        <select
                                            className="form-control"
                                            value={headerData.GroupWorkKey}
                                            onChange={handleGroupWorkChange}
                                            disabled={!headerData.MissionKey}
                                        >
                                            <option value="">-- เลือกกลุ่มงาน --</option>
                                            {filteredGroups.map((g) => (
                                                <option key={g.key} value={g.key}>
                                                    {g.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="font-weight-bold">งาน / ฝ่าย</label>
                                        <select
                                            className="form-control"
                                            value={headerData.DepartmentKey}
                                            onChange={handleDepartmentChange}
                                            disabled={!headerData.GroupWorkKey}
                                        >
                                            <option value="">-- เลือกงาน/ฝ่าย --</option>
                                            {filteredDepts.map((d) => (
                                                <option key={d.key} value={d.key}>
                                                    {d.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* 2. ข้อมูลโครงการ */}
                            <div className="p-3 mb-3 bg-light border rounded">
                                <h5
                                    className="font-weight-bold text-info"
                                    style={{ marginTop: 0 }}
                                >
                                    <i className="fas fa-folder"></i> 2. ข้อมูลโครงการ
                                </h5>
                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="font-weight-bold">
                                            ชื่อโครงการ{projectItems.length > 0 ? " *" : ""}
                                        </label>
                                        <input
                                            type="text"
                                            aria-label="ชื่อโครงการ"
                                            className="form-control"
                                            placeholder="เช่น โครงการพัฒนาระบบเทคโนโลยีสารสนเทศ"
                                            value={projectData.ProjectName}
                                            onChange={(e) =>
                                                setProjectData({
                                                    ...projectData,
                                                    ProjectName: e.target.value
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>ความสอดคล้องยุทธศาสตร์</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="ระบุประเด็นยุทธศาสตร์"
                                            value={projectData.StrategicAlignment}
                                            onChange={(e) =>
                                                setProjectData({
                                                    ...projectData,
                                                    StrategicAlignment: e.target.value
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="col-md-12 mt-2">
                                        <label>วัตถุประสงค์</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            placeholder="ระบุวัตถุประสงค์โครงการ"
                                            value={projectData.Objective}
                                            onChange={(e) =>
                                                setProjectData({
                                                    ...projectData,
                                                    Objective: e.target.value
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            </fieldset>
                            {(!canEdit || requestStatus === "Need_Revision") && <BudgetApprovalReview key={`${requestKey}-${requestStatus}-${approvalLevel}`}
                                items={requestItems} capitalCategories={CAPITAL_CATEGORIES} operatingCategories={OPERATING_CATEGORIES}
                                approvalLevel={approvalLevel} busy={isSaving} onSubmit={handleReviewSubmit} getOperatingSubcategories={getOperatingSubcategories} />}
                            {canEdit && <>
                            <h5 className={classes.sectionTitle}>3. หมวดงบประมาณ</h5>
                            <div className="btn-group mb-3 w-100" role="group" aria-label="หมวดงบประมาณหลัก">
                                <button type="button" aria-label="งบลงทุน" aria-pressed={isDurable}
                                    className={`btn ${isDurable ? "btn-info" : "btn-outline-info"}`}
                                    disabled={!CAPITAL_CATEGORIES.length}
                                    onClick={() => setBudgetCategory(CAPITAL_CATEGORIES[0].key)}>
                                    1. งบลงทุน ({projectItems.length})
                                </button>
                                <button type="button" aria-label="งบดำเนินงาน" aria-pressed={!isDurable}
                                    className={`btn ${!isDurable ? "btn-info" : "btn-outline-info"}`}
                                    onClick={() => setBudgetCategory(7)}>
                                    2. งบดำเนินงาน ({requestItems.length - projectItems.length})
                                </button>
                            </div>

                            {!isDurable && (
                                <div className="btn-group mb-3 w-100" role="group" aria-label="ประเภทงบดำเนินงาน">
                                    <button type="button" aria-label="งบบุคลากร" aria-pressed={budgetCategory === 7}
                                        className={`btn ${budgetCategory === 7 ? "btn-info" : "btn-outline-info"}`}
                                        onClick={() => setBudgetCategory(7)}>
                                        งบบุคลากร ({getActiveList(7).length})
                                    </button>
                                    <button type="button" aria-label="หมวดย่อยงบดำเนินงาน" aria-pressed={budgetCategory !== 7}
                                        className={`btn ${budgetCategory !== 7 ? "btn-info" : "btn-outline-info"}`}
                                        disabled={!OPERATING_CATEGORIES.length}
                                        onClick={() => setBudgetCategory(OPERATING_CATEGORIES[0].key)}>
                                        งบดำเนินงาน ({requestItems.length - projectItems.length - getActiveList(7).length})
                                    </button>
                                </div>
                            )}

                            {budgetCategory !== PERSONNEL_CATEGORY.key && (
                                <div className="d-flex flex-wrap mb-3" role="group" aria-label="หมวดงบประมาณย่อย" style={{ gap: "8px" }}>
                                    {(isDurable ? CAPITAL_CATEGORIES : OPERATING_CATEGORIES).map(category => (
                                        <button key={category.key} type="button" aria-label={category.label} aria-pressed={budgetCategory === category.key}
                                            className={`btn ${budgetCategory === category.key ? "btn-info" : "btn-outline-info"}`}
                                            onClick={() => setBudgetCategory(category.key)}>
                                            {category.label} ({getActiveList(category.key).length})
                                        </button>
                                    ))}
                                </div>
                            )}
                            <p className="text-info font-weight-bold">
                                {isDurable ? "งบลงทุน" : budgetCategory === 7 ? "งบดำเนินงาน" : "งบดำเนินงาน / งบดำเนินงาน"} / {getCategoryLabel(budgetCategory)}
                            </p>

                            {/* 4. ฟอร์มเพิ่มรายการด่วน */}
                            <h5 className={classes.sectionTitle}>
                                <i className="fas fa-plus-circle"></i>{" "}
                                {canEdit ? "เพิ่มรายการ" : "รายการ"}{getCategoryLabel(budgetCategory)}
                            </h5>
                            <RequestItemForm
                                key={budgetCategory}
                                readOnly={!canEdit}
                                onAdd={handleAddItem}
                                selectedItemType={budgetCategory}
                                subcategoryCounts={getSubcategoryCounts(budgetCategory)}
                                onSubcategoryChange={setSelectedSubcategory}
                            />

                            {!isDurable && selectedSubcategory?.categoryKey === budgetCategory && subcategoryDetails
                                .filter(c => c.categoryKey === budgetCategory && c.label === selectedSubcategory.label && c.allowFileUpload)
                                .map(c => <div key={c.key} className="mt-4 mb-3">
                                    <h5>ไฟล์ PDF ส่วนกลางของหมวดย่อย: {c.label}</h5>
                                    <BudgetFileUpload subcategoryKey={c.key} disabled={isSaving || !canEdit} refreshKey={filesRefreshKey}
                                        pendingFiles={pendingSubcategoryFiles[c.key] || []}
                                        onSelectFiles={files => setPendingSubcategoryFiles(prev => ({ ...prev, [c.key]: [...(prev[c.key] || []), ...files] }))} />
                                </div>)}

                            {isSaving && <p role="status">กำลังบันทึกรายการและอัปโหลดไฟล์ กรุณารอสักครู่...</p>}
                            {/* 5. ตารางสรุปรายการคำขอ */}
                            {currentList.length > 0 && (
                                <div style={{ marginTop: "25px" }}>
                                    <h5 className={classes.sectionTitle}>
                                        <i className="fas fa-list"></i> รายการที่เพิ่มในหมวดนี้ (
                                        {currentList.length} รายการ)
                                    </h5>
                                    <table
                                        className="table table-bordered table-striped"
                                        style={{ fontSize: "13px" }}
                                    >
                                        <thead className="thead-light">
                                            <tr>
                                                <th width="40" className="text-center">#</th>
                                                {showItemCategory && <th width="180">{isDurable ? "หมวดหมู่" : "หมวดย่อย"}</th>}
                                                <th>ชื่อรายการ</th>
                                                <th width="80" className="text-right">จำนวน</th>
                                                <th width="120" className="text-right">ราคา/หน่วย</th>
                                                <th width="130" className="text-right">รวมเป็นเงิน</th>
                                                <th width="130" className="text-center">ความสมบูรณ์</th>
                                                <th width="100" className="text-center">จัดการ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentList.map((item, idx) => {
                                                const repQty = isDurable ? parseInt(item.ReplacementQty || 0) : 0;
                                                const newQty = parseInt(item.NewPurchaseQty || 0);
                                                const reqQty = repQty + newQty;
                                                const total = reqQty * parseFloat(item.UnitPrice || 0);
                                                const hasDetails = item.Reason && item.Reason.trim() !== "";

                                                return (
                                                    <tr key={item.ClientKey || item.ItemKey || idx}>
                                                        <td className="text-center">{idx + 1}</td>
                                                        {showItemCategory && (
                                                            <td>
                                                                <span
                                                                    className={`badge badge-${
                                                                        item.ItemCategory === "ครุภัณฑ์"
                                                                            ? "info"
                                                                            : "primary"
                                                                    }`}
                                                                >
                                                                    {isDurable ? getCategoryLabel(getItemCategoryKey(item)) : item.ItemCategory || "ยังไม่ระบุหมวดย่อย"}
                                                                </span>
                                                            </td>
                                                        )}
                                                        <td>
                                                            <strong>{item.ItemName}</strong>
                                                            <BudgetFileUpload itemKey={item.ItemKey} fileName={item.FileName} disabled={isSaving || !canEdit}
                                                                pendingFiles={item.PendingFile ? [item.PendingFile] : []}
                                                                onSelectFiles={files => setRequestItems(prev => prev.map(i => i === item ? { ...i, PendingFile: files[0] } : i))} />
                                                        </td>
                                                        <td className="text-right font-weight-bold text-primary">
                                                            {reqQty}
                                                        </td>
                                                        <td className="text-right">
                                                            {parseFloat(item.UnitPrice || 0).toLocaleString()}
                                                        </td>
                                                        <td className="text-right font-weight-bold text-danger">
                                                            {total.toLocaleString()}
                                                        </td>
                                                        <td className="text-center">
                                                            {hasDetails ? (
                                                                <span className="text-success">
                                                                    <i className="fas fa-check-circle"></i> ครบถ้วน
                                                                </span>
                                                            ) : (
                                                                <span className="text-warning">
                                                                    <i className="fas fa-exclamation-triangle"></i> ยังไม่ใส่เหตุผล
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <button
                                                                className="btn btn-sm btn-info p-1 mr-1"
                                                                onClick={() => handleOpenEdit(activeItems.indexOf(item))}
                                                                title={canEdit ? "แก้ไข" : "ดูรายละเอียด"}
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>
                                                            {canEdit && <button
                                                                className="btn btn-sm btn-danger p-1"
                                                                onClick={() => handleRemoveItem(activeItems.indexOf(item))}
                                                                title="ลบ"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            </>}
                            </fieldset>
                        </CardBody>
                        <CardFooter style={{ justifyContent: "space-between" }}>
                            <Button color="transparent" onClick={() => history.goBack()}>
                                ย้อนกลับ
                            </Button>
                            <div>
                                {canEdit && <>
                                <Button
                                    color="warning"
                                    disabled={isSaving} onClick={() => handleSave("Draft")}
                                    style={{ marginRight: "10px" }}
                                >
                                    บันทึกร่าง
                                </Button>
                                <Button color="success" disabled={isSaving} onClick={() => handleSave("Submitted")}>
                                    ส่งคำขอ
                                </Button>
                                </>}
                            </div>
                        </CardFooter>
                    </Card>
                </Col>
            </Grid>

            {/* Modal รายละเอียด & Checklist */}
            <Modal
                isOpen={isEditModalOpen}
                onRequestClose={() => setIsEditModalOpen(false)}
                style={{ overlay: styles.modalOverlay, content: styles.modalContent }}
            >
                <h4
                    style={{
                        borderBottom: "1px solid #ddd",
                        paddingBottom: "10px",
                        marginTop: 0,
                        color: "#00acc1"
                    }}
                >
                    <i className="fas fa-edit"></i> รายละเอียด: {editFormData.ItemName}
                </h4>

                <fieldset disabled={!canEdit}>
                <section className={classes.detailSection}>
                    <h5>ข้อมูลรายการ</h5>
                <div className="row">
                    {isCapitalCategory(editTarget.category) || editSubcategories.length > 0 ? (
                        <>
                            <div className="col-md-5">
                                <label className="font-weight-bold">{editSubcategories.length ? "หมวดย่อยรายการ" : "หมวดหมู่รายการ"}</label>
                                {editSubcategories.length > 0 ? (
                                    <select name="ItemCategory" aria-label="หมวดย่อยรายการที่แก้ไข"
                                        className="form-control" value={editFormData.ItemCategory || ""} onChange={handleEditChange}>
                                        <option value="">-- เลือกหมวดย่อย --</option>
                                        {editFormData.ItemCategory && !editSubcategories.includes(editFormData.ItemCategory) && (
                                            <option value={editFormData.ItemCategory}>{editFormData.ItemCategory} ({editSubcategoryCounts[editFormData.ItemCategory] || 0}) (รายการเดิม)</option>
                                        )}
                                        {editSubcategories.map(label => <option key={label} value={label}>{label} ({editSubcategoryCounts[label] || 0})</option>)}
                                    </select>
                                ) : (
                                    <input className="form-control font-weight-bold" value={getCategoryLabel(editTarget.category)} readOnly />
                                )}
                            </div>
                            <div className="col-md-7">
                                <label className="font-weight-bold">ชื่อรายการ</label>
                                <input
                                    type="text"
                                    name="ItemName"
                                    className="form-control"
                                    value={editFormData.ItemName || ""}
                                    onChange={handleEditChange}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="col-md-12">
                            <label className="font-weight-bold">ชื่อรายการ</label>
                            <input
                                type="text"
                                name="ItemName"
                                className="form-control"
                                value={editFormData.ItemName || ""}
                                onChange={handleEditChange}
                            />
                        </div>
                    )}
                </div>

                </section>
                <section className={classes.detailSection}>
                    <h5>จำนวนและราคา</h5>
                    <div className={classes.quantityGrid} style={{ "--quantity-columns": isCapitalCategory(editTarget.category) ? 5 : 4 }}>
                        {[
                            ["ExistingQty", "มีอยู่เดิม", ""],
                            ["TotalRequiredQty", "ความต้องการทั้งสิ้น", ""],
                            ...(isCapitalCategory(editTarget.category) ? [["ReplacementQty", "ขอทดแทน", "text-warning"]] : []),
                            ["NewPurchaseQty", isCapitalCategory(editTarget.category) ? "ขอซื้อใหม่" : "จำนวนที่ขอ", "text-success"],
                            ["UnitPrice", "ราคาต่อหน่วย (บาท)", ""]
                        ].map(([name, label, color]) => <div key={name}>
                            <label htmlFor={`detail-${name}`} className={`font-weight-bold ${color}`}>{label}</label>
                            <input id={`detail-${name}`} type="number" name={name} className="form-control"
                                value={editFormData[name] || 0} onChange={handleEditChange} />
                        </div>)}
                    </div>
                <div className="row mt-3">
                    {editTarget.category === 1 && parseInt(editFormData.ReplacementQty || 0) > 0 && (
                        <div className="col-md-6">
                            <label>อายุการใช้งานของเดิม (ปี/สภาพ)</label>
                            <input
                                type="text"
                                name="OldItemAge"
                                className="form-control"
                                placeholder="เช่น ใช้งานมาแล้ว 8 ปี ชำรุด"
                                value={editFormData.OldItemAge || ""}
                                onChange={handleEditChange}
                            />
                        </div>
                    )}
                </div>

                </section>
                <section className={classes.detailSection}>

                <h6 className="font-weight-bold text-secondary">
                    <i className="fas fa-paperclip"></i> เอกสารแนบประกอบรายการ{" "}
                    {editFormData.ItemCategory && `(${editFormData.ItemCategory})`}
                </h6>

                <div className={classes.checklistGrid}>
                    {(editFormData.ItemCategory === "สิ่งก่อสร้าง" ? [
                        ["HasBlueprint", "มีแบบแปลน"],
                        ["HasBOQ", "มีใบประมาณราคา (BOQ)"]
                    ] : [
                        ["IsStandardPrice", "ราคามาตรฐานกลาง"],
                        ["HasSpec", "มีคุณลักษณะเฉพาะ (Spec)"],
                        ["HasQuotation", "มีใบสืบราคา / ใบเสนอราคา"],
                        ...(editTarget.category === 1 ? [["HasTechAssessment", "มีแบบประเมินความคุ้มค่าทางเทคโนโลยี"]] : [])
                    ]).map(([name, label]) => (
                        <label key={name} className={classes.checklistOption}
                            style={editFormData[name] ? { borderColor: "#00acc1", backgroundColor: "#e8f8fa" } : undefined}>
                            <input type="checkbox" name={name} checked={editFormData[name] || false} onChange={handleEditChange} />
                            <span>{label}</span>
                        </label>
                    ))}
                </div>
                </section>
                <section className={classes.detailSection}>
                <div className="row mt-3">
                    <div className="col-md-12">
                        <label className="font-weight-bold text-danger">
                            เหตุผลความจำเป็นในการจัดซื้อ *
                        </label>
                        <textarea
                            name="Reason"
                            className="form-control"
                            rows="3"
                            placeholder="ระบุเหตุผลความจำเป็น..."
                            value={editFormData.Reason || ""}
                            onChange={handleEditChange}
                        />
                    </div>
                </div>

                </section>
                </fieldset>
                <div className={classes.modalActions}>
                    <Button color="transparent" onClick={() => setIsEditModalOpen(false)}>
                        ยกเลิก
                    </Button>
                    {canEdit && <Button color="primary" onClick={handleSaveEdit}>
                        <i className="fas fa-save"></i> บันทึกรายละเอียด
                    </Button>}
                </div>
            </Modal>
        </div>
    );
};

export default BudgetForm;
